import { ExternalLink, Ship } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useCompanies } from '../lib/api';
import type { DrillcoDto, OilcoDto } from '../types';
import { Flag } from './_shared';

// Tab 6 — Companies. Drilling contractors and oil companies. Fleet counts reflect rigs
// tracked in WWD (computed, never stored), so contractors without ingested fleets honestly
// show zero until their adapters land.

export default function Companies() {
  const { data, isLoading, error } = useCompanies();

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error || !data) return <ErrorState message={(error as Error | null)?.message} />;

  const { drillcos, oilcos } = data;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Companies</CardTitle>
        <CardDescription className="mt-2">
          {drillcos.length} drilling contractors and {oilcos.length} oil companies. Fleet and
          contract counts reflect rigs tracked in WWD.
        </CardDescription>
      </Card>

      <section className="mb-6">
        <span className="eyebrow">Drilling contractors</span>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {drillcos.map((d) => (
            <DrillcoCard key={d.slug} drillco={d} />
          ))}
        </div>
      </section>

      <section>
        <span className="eyebrow">Oil companies</span>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {oilcos.map((o) => (
            <OilcoCard key={o.slug} oilco={o} />
          ))}
        </div>
      </section>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function DrillcoCard({ drillco }: { drillco: DrillcoDto }) {
  const { fleet } = drillco;
  const tracked = fleet.floaters + fleet.jackups + fleet.platforms;
  const chips = [
    { label: 'floaters', value: fleet.floaters },
    { label: 'jack-ups', value: fleet.jackups },
    { label: 'platforms', value: fleet.platforms },
  ];

  return (
    <div className="flex flex-col bg-surface border border-border rounded-(--radius-md) p-5 shadow-[var(--shadow-sm)]">
      <div className="flex flex-wrap items-center gap-2">
        <Flag countryCode={drillco.countryCode} />
        <h3 className="text-lg font-semibold text-text">{drillco.name}</h3>
        {drillco.ticker && (
          <span className="font-(family-name:--font-ui) text-xs text-muted [font-variant-numeric:tabular-nums]">
            {drillco.ticker}
          </span>
        )}
        {!drillco.active && (
          <span className="ml-auto rounded-(--radius-sm) border border-accent-warn px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-accent-warn">
            Inactive
          </span>
        )}
      </div>

      {drillco.description && <p className="mt-2 text-sm text-muted">{drillco.description}</p>}

      <div className="mt-3">
        {tracked === 0 ? (
          <p className="text-xs italic text-muted opacity-70">No rigs ingested yet.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((c) => (
              <span
                key={c.label}
                className="inline-flex items-center gap-1 rounded-(--radius-sm) border border-border bg-surface-elevated px-2 py-0.5 text-[0.7rem] text-muted font-(family-name:--font-ui)"
              >
                <span className="font-semibold text-text [font-variant-numeric:tabular-nums]">
                  {c.value}
                </span>
                {c.label}
              </span>
            ))}
            <Link
              to={`/wwd/rigs?drillco=${drillco.slug}`}
              className="inline-flex items-center gap-1 text-[0.7rem] text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
            >
              <Ship className="h-3 w-3 flex-shrink-0" aria-hidden />
              View rigs
            </Link>
          </div>
        )}
        <p className="mt-1 text-[0.65rem] uppercase tracking-[0.12em] text-muted font-(family-name:--font-ui)">
          tracked in WWD
        </p>
      </div>

      {drillco.keyRegions.length > 0 && (
        <p className="mt-3 text-sm">
          <span className="text-[0.7rem] uppercase tracking-[0.14em] text-muted font-(family-name:--font-ui)">
            Key regions:&nbsp;
          </span>
          {drillco.keyRegions.map((r) => r.name).join(', ')}
        </p>
      )}

      {drillco.latestFsr && (
        <a
          href={drillco.latestFsr.url}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
          {drillco.latestFsr.title}
        </a>
      )}
    </div>
  );
}

function OilcoCard({ oilco }: { oilco: OilcoDto }) {
  return (
    <div className="flex flex-col bg-surface border border-border rounded-(--radius-md) p-5 shadow-[var(--shadow-sm)]">
      <div className="flex flex-wrap items-center gap-2">
        <Flag countryCode={oilco.countryCode} />
        <h3 className="text-lg font-semibold text-text">{oilco.name}</h3>
      </div>

      {oilco.description && <p className="mt-2 text-sm text-muted">{oilco.description}</p>}

      <p className="mt-3 text-sm">
        <span className="font-semibold text-text [font-variant-numeric:tabular-nums]">
          {oilco.contractedRigs}
        </span>{' '}
        <span className="text-muted">
          contracted {oilco.contractedRigs === 1 ? 'rig' : 'rigs'} tracked in WWD
        </span>
      </p>

      {oilco.keyRegions.length > 0 && (
        <p className="mt-2 text-sm">
          <span className="text-[0.7rem] uppercase tracking-[0.14em] text-muted font-(family-name:--font-ui)">
            Key regions:&nbsp;
          </span>
          {oilco.keyRegions.map((r) => r.name).join(', ')}
        </p>
      )}

      {oilco.website && (
        <a
          href={oilco.website}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
          {hostOf(oilco.website)}
        </a>
      )}
    </div>
  );
}
